/**
 * These settings are unique to each device, so to save DB calls and possible bugs we're storing them on the device
 */

import { registerUserStatusCallback } from "@utils/helpers/unload-promise"
import type { DarkModeOptions } from "@utils/material/darkmode"
import { deviceID } from "@utils/underhood/device"
import { user, userStatus } from "@utils/underhood/firebase"
import { derived, get, writable } from "svelte/store"

type DeviceSettings = {
    fontSize: number
    darkMode: DarkModeOptions
    easyFont?: boolean
}

const defaultSettings = (): DeviceSettings => ({
    fontSize: 1,
    darkMode: "auto",
})

/**
 * This key combines the device ID and the email to create a unique string
 * @returns string representing unique ID combined with email
 */
function getKey() {
    const userData = get(user)
    return userData ? userData.email + deviceID : deviceID
}

const thisDeviceSettings = writable<DeviceSettings>(processDeviceSettings())

/**
 * Finds device settings that match current profile, returns default if not
 * @returns DeviceSettings if stored and matches key, or default if none found
 */
function processDeviceSettings() {
    // Get current key and see if there is a match in local storage
    let key: string = getKey()
    const str = localStorage.getItem(key)

    let settings: DeviceSettings
    if (!str) {
        // nothing stored, use defaults
        settings = defaultSettings()
        localStorage.setItem(key, JSON.stringify(settings))
    } else {
        settings = JSON.parse(str) as DeviceSettings
    }
    return settings
}

/**
 * Saves `thisDeviceSettings` store to Local Storage
 */
function saveDeviceData() {
    const key = getKey()
    const payload = JSON.stringify(get(thisDeviceSettings))
    localStorage.setItem(key, payload)
}

//
// Font size
//

export const fontSize = writable<number>(get(thisDeviceSettings).fontSize)
fontSize.subscribe(async (size) => {
    get(thisDeviceSettings).fontSize = size
    saveDeviceData()
})

//
// Darkmode
//

export const darkmodePreference = writable<DarkModeOptions>(
    get(thisDeviceSettings).darkMode
)
darkmodePreference.subscribe((darkMode) => {
    get(thisDeviceSettings).darkMode = darkMode
    saveDeviceData()
})

//
// Darkmode
//

export const easyFontPreference = writable<boolean>(
    !!get(thisDeviceSettings).easyFont
)
easyFontPreference.subscribe((easyFont) => {
    get(thisDeviceSettings).easyFont = easyFont
    saveDeviceData()
})

///
// Listen to all when user changes
//

thisDeviceSettings.subscribe((settings) => {
    fontSize.set(settings.fontSize)
    darkmodePreference.set(settings.darkMode)
    easyFontPreference.set(settings.easyFont ?? false)
})

registerUserStatusCallback({
    unload: async () => {
        // Defaults
    },
    load: async (status) => {
        thisDeviceSettings.set(processDeviceSettings())
    },
})

/**
 * Simply adds a class to navigator
 */
easyFontPreference.subscribe((easyFont) => {
    if (easyFont) {
        document.documentElement.classList.add("easy-font")
    } else {
        document.documentElement.classList.remove("easy-font")
    }
})

/**
 * Listener that scales font size according to
 * device settings. Manages using CSS `font-size: [x]em`
 * on target node
 * @param node
 * @returns
 */
export function styleFontSize(node: HTMLElement) {
    const unsub = fontSize.subscribe((n) => {
        node.style.fontSize = n + "em"
    })
    return {
        destroy() {
            unsub()
        },
    }
}
